/**
 * Unified Chart Factory
 * Single source of truth for all chart configurations
 * Eliminates duplication between "premium" and "regular" charts
 * 
 * Architecture:
 * - BaseChartFactory: Common configurations and utilities
 * - ChartTypeFactory: Type-specific configurations
 * - UnifiedChartFactory: Main entry point with backward compatibility
 */

import { EChartsOption } from 'echarts';
import { WeeklyDataPoint, BRAND_COLORS, TYPOGRAPHY } from '../types/data';
import { TitleUtils, TitleContext } from '../utils/titleUtils';
import { getResponsiveFontSizes } from '../utils/responsiveChartUtils';

// ============================================================================
// TYPES AND INTERFACES
// ============================================================================

export enum ChartType {
  // Horizontal Bar Charts
  COUNTRY_FUNDING = 'country-funding',
  ACQUISITION_CATEGORY = 'acquisition-category',
  PRODUCT_CATEGORY = 'product-category',

  // Vertical Bar Charts
  YEAR_OVER_YEAR = 'year-over-year',
  FUNDING_TIMESERIES = 'funding-timeseries',
  COMPARISON = 'comparison',
  YOY_FUNDING = 'yoy-funding',
  YOY_ACQUISITIONS = 'yoy-acquisitions'
}

export enum ChartOrientation {
  HORIZONTAL = 'horizontal',
  VERTICAL = 'vertical'
}

export enum DataType {
  CURRENCY = 'currency',
  COUNT = 'count',
  PERCENTAGE = 'percentage'
}

export interface ChartConfig {
  type: ChartType;
  orientation: ChartOrientation;
  dataType: DataType;
  title: string;
  subtitle?: string;
  data: any;
  options?: {
    showDataLabels?: boolean;
    showLegend?: boolean;
    showGrid?: boolean;
    animation?: boolean;
    height?: number;
    customColors?: string[];
    // Dynamic title generation options
    enableDynamicTitles?: boolean;
    chartContext?: 'default' | 'social-media' | 'email' | 'presentation';
    timeGrouping?: 'weeks' | 'months' | 'quarters';
    timeframe?: '1w' | '2w' | '4w' | '12w' | '6m' | 'ytd' | 'ttm' | 'custom';
    yearVisibility?: {
      show2023?: boolean;
      show2024?: boolean;
      show2025?: boolean;
    };
    // For YoY charts
    isMA?: boolean;
    isTTM?: boolean;
    // Time domain context
    timeDomain?: {
      startDate?: string;
      endDate?: string;
      periodLabel?: string;
      weekOffset?: number;
    };
    // Week offset for navigation
    weekOffset?: number;
    // Carta-style data description (legacy string support)
    dataDescription?: string;
    // Context for generating proper data descriptions
    titleContext?: TitleContext;
  };
}

export interface BaseChartOptions {
  grid: any;
  tooltip: any;
  legend: any;
  animation: any;
  graphic: any[];
}

// ============================================================================
// BASE CHART FACTORY
// ============================================================================

export class BaseChartFactory {
  public static readonly PREMIUM_COLORS = {
    primary: '#633FF3',
    secondary: '#222222',
    accent: '#00C851',
    warning: '#FF4444',
    danger: '#EF4444',
    surface: '#FFFFFF',
    background: '#F8FAFC',
    text: '#222222',
    textSecondary: '#666666',
    textMuted: '#999999',
    border: '#E8E8E8',
    gridLines: '#E5E7EB', // Light gray for visible grid lines
    dataSource: '#999999' // Light gray for data source attribution
  };


  // ============================================================================
  // CHART-TYPE-SPECIFIC STANDARDS (ECharts 5.6.0 2025)
  // ============================================================================
  // Each chart type has unique layout requirements and best practices
  // All spacing is controlled from these centralized settings
  public static readonly CHART_TYPE_STANDARDS = {
    
    // ┌─────────────────────────────────────────────────────────────────────────┐
    // │ HORIZONTAL BAR CHARTS (Country, Product Category, Acquisition Category) │
    // └─────────────────────────────────────────────────────────────────────────┘
    horizontalBar: {
      // Container Height Configuration
      height: {
        default: 800,           // Default height in pixels
        min: 700,              // Minimum height in pixels
        max: 1200,             // Maximum height in pixels
        responsive: true       // Whether to use responsive height
      },
      
      // Title Configuration
      title: {
        get fontSize() { return getResponsiveFontSizes().title; }, // Responsive title font size
        fontWeight: 700,        // Title font weight
        color: '#222222',       // Title color
        alignment: 'left',      // Title alignment: 'left', 'center', 'right'
        margins: {
          top: '1%',            // Distance from top of chart container
          bottom: 0,            // Distance below title
          left: '1%',           // Distance from left edge
          right: '1%'           // Distance from right edge
        }
      },

      // Subtitle Configuration
      subtitle: {
        get fontSize() { return getResponsiveFontSizes().subtitle; }, // Responsive subtitle font size
        fontWeight: 500,        // Subtitle font weight
        color: '#666666',       // Subtitle color
        alignment: 'left',      // Subtitle alignment: 'left', 'center', 'right'
        margins: {
          top: 8,               // Gap between title and subtitle (pixels)
          bottom: 0,            // Distance below subtitle
          left: '1%',           // Distance from left edge
          right: '1%'           // Distance from right edge
        }
      },

      // Data Description Configuration
      dataDescription: {
        get fontSize() { return getResponsiveFontSizes().dataDescription; }, // Responsive data description text size
        fontWeight: 400,        // Data description font weight
        color: '#666666',       // Data description color
        alignment: 'left',      // Data description alignment: 'left', 'center', 'right'
        margins: {
          top: 8,               // Gap between subtitle and data description (pixels)
          bottom: 0,            // Distance below data description
          left: '1%',           // Distance from left edge
          right: '1%'           // Distance from right edge
        },
        chartGap: 20            // Gap between data description and chart area (pixels)
      },

      // Chart Area Configuration
      chartArea: {
        margins: {
          top: '15%',           // Chart area starts here (dynamically calculated)
          bottom: '12%',        // Chart area ends here (standardized for horizontal bars)
          left: '1%',           // Chart area left margin
          right: '1%'           // Chart area right margin
        }
      },

      // Legend Configuration
      legend: {
        get fontSize() { return getResponsiveFontSizes().legend; }, // Responsive legend text size
        fontWeight: 400,        // Legend font weight
        color: '#666666',       // Legend color
        alignment: 'center',    // Legend alignment: centered for all charts
        margins: {
          top: '1%',            // Legend position from top
          bottom: 0,            // Distance below legend
          left: '1%',           // Distance from left edge
          right: '1%'           // Distance from right edge
        },
        itemGap: 15,            // Gap between legend items (pixels) - standardized
        chartAreaGap: 20,       // Gap between chart area bottom and legend (pixels)
        ctaGap: 40              // Gap between legend and CTA (pixels)
      },

      // CTA (Call-to-Action) Configuration
      cta: {
        get fontSize() { return getResponsiveFontSizes().cta; }, // Responsive CTA text size
        fontWeight: 400,        // CTA font weight
        color: '#222222',       // CTA color
        alignment: 'center',    // CTA alignment: 'left', 'center', 'right'
        margins: {
          top: 0,               // Distance above CTA
          bottom: '2%',         // CTA position from bottom
          left: '1%',           // Distance from left edge
          right: '1%'           // Distance from right edge
        }
      },
      
      // Bar Styling
      bar: {
        height: 40,             // Height of horizontal bars
        gap: 8                  // Gap between bars
      }
    },
    
    // ┌─────────────────────────────────────────────────────────────────────────┐
    // │ VERTICAL BAR CHARTS (Funding by Quarter, etc.)                         │
    // └─────────────────────────────────────────────────────────────────────────┘
    verticalBar: {
      // Container Height Configuration
      height: {
        default: 800,           // Default height in pixels
        min: 700,              // Minimum height in pixels
        max: 1200,             // Maximum height in pixels
        responsive: true       // Whether to use responsive height
      },
      
      // Title Configuration
      title: {
        get fontSize() { return getResponsiveFontSizes().title; }, // Responsive title font size
        fontWeight: 700,        // Title font weight
        color: '#222222',       // Title color
        alignment: 'left',      // Title alignment: 'left', 'center', 'right'
        margins: {
          top: '1%',            // Distance from top of chart container
          bottom: 0,            // Distance below title
          left: '1%',           // Distance from left edge
          right: '1%'           // Distance from right edge
        }
      },

      // Subtitle Configuration
      subtitle: {
        get fontSize() { return getResponsiveFontSizes().subtitle; }, // Responsive subtitle font size
        fontWeight: 500,        // Subtitle font weight
        color: '#666666',       // Subtitle color
        alignment: 'left',      // Subtitle alignment: 'left', 'center', 'right'
        margins: {
          top: 8,               // Gap between title and subtitle (pixels)
          bottom: 0,            // Distance below subtitle
          left: '1%',           // Distance from left edge
          right: '1%'           // Distance from right edge
        }
      },

      // Data Description Configuration
      dataDescription: {
        get fontSize() { return getResponsiveFontSizes().dataDescription; }, // Responsive data description text size
        fontWeight: 400,        // Data description font weight
        color: '#666666',       // Data description color
        alignment: 'left',      // Data description alignment: 'left', 'center', 'right'
        margins: {
          top: 48,               // Gap between subtitle and data description (pixels)
          bottom: 0,            // Distance below data description
          left: '1%',           // Distance from left edge
          right: '1%'           // Distance from right edge
        },
        chartGap: 40            // Gap between data description and chart area (pixels)
      },

      // Chart Area Configuration
      chartArea: {
        margins: {
          top: '15%',           // Chart area starts here (dynamically calculated)
          bottom: '15%',        // Chart area ends here (more space for x-axis labels)
          left: '1%',           // Chart area left margin
          right: '1%'           // Chart area right margin
        }
      },

      // Legend Configuration
      legend: {
        get fontSize() { return getResponsiveFontSizes().legend; }, // Responsive legend text size
        fontWeight: 400,        // Legend font weight
        color: '#666666',       // Legend color
        alignment: 'center',      // Legend alignment: 'left', 'center', 'right'
        margins: {
          top: '1%',            // Legend position from top
          bottom: 0,            // Distance below legend
          left: '1%',           // Distance from left edge
          right: '1%'           // Distance from right edge
        },
        itemGap: 15,            // Gap between legend items (pixels) - standardized
        chartAreaGap: 25,       // Gap between chart area bottom and legend (pixels)
        ctaGap: 70              // Gap between legend and CTA (pixels)
      },

      // CTA (Call-to-Action) Configuration
      cta: {
        get fontSize() { return getResponsiveFontSizes().cta; }, // Responsive CTA text size
        fontWeight: 400,        // CTA font weight
        color: '#222222',       // CTA color
        alignment: 'center',    // CTA alignment: 'left', 'center', 'right'
        margins: {
          top: 1,               // Distance above CTA
          bottom: '2%',         // CTA position from bottom
          left: '1%',           // Distance from left edge
          right: '1%'           // Distance from right edge
        }
      },
      
      // Bar Styling
      bar: {
        width: 40,              // Width of vertical bars
        gap: 8                  // Gap between bars
      }
    },
    
    // ┌─────────────────────────────────────────────────────────────────────────┐
    // │ LINE CHARTS (Trend Analysis)                                           │
    // └─────────────────────────────────────────────────────────────────────────┘
    line: {
      // Container Height Configuration
      height: {
        default: 800,           // Default height in pixels
        min: 700,              // Minimum height in pixels
        max: 1200,             // Maximum height in pixels
        responsive: true       // Whether to use responsive height
      },
      
      title: {
        fontSize: 32,
        topMargin: '1%',
        leftMargin: '1%',
        subtitleGap: 16
      },
      dataDescription: {
        fontSize: 16,
        gap: 8,
        topPosition: '10%',
        chartGap: 20
      },
      chartArea: {
        margins: {
          top: '15%',           // Chart area starts here (dynamically calculated)
          bottom: '15%',        // Chart area ends here
          left: '1%',           // Chart area left margin
          right: '1%'           // Chart area right margin
        }
      },
      legend: {
        fontSize: 16,           // Legend text size
        fontWeight: 400,        // Legend font weight
        color: '#666666',       // Legend color
        alignment: 'center',    // Legend alignment: centered for all charts
        margins: {
          top: '1%',            // Legend position from top
          bottom: 0,            // Distance below legend
          left: '1%',           // Distance from left edge
          right: '1%'           // Distance from right edge
        },
        itemGap: 15,            // Gap between legend items (pixels) - standardized
        chartAreaGap: 40,       // Gap between chart area bottom and legend (pixels)
        ctaGap: 80              // Gap between legend and CTA (pixels)
      },
      cta: {
        fontSize: 20,           // CTA text size
        fontWeight: 400,        // CTA font weight
        color: '#222222',       // CTA color
        alignment: 'center',    // CTA alignment: 'left', 'center', 'right'
        margins: {
          top: 0,               // Distance above CTA
          bottom: '1%',         // CTA position from bottom
          left: '1%',           // Distance from left edge
          right: '1%'           // Distance from right edge
        }
      }
    },
    
    // ┌─────────────────────────────────────────────────────────────────────────┐
    // │ PIE CHARTS (if any)                                                    │
    // └─────────────────────────────────────────────────────────────────────────┘
    pie: {
      // Container Height Configuration
      height: {
        default: 800,           // Default height in pixels
        min: 700,              // Minimum height in pixels
        max: 1200,             // Maximum height in pixels
        responsive: true       // Whether to use responsive height
      },
      
      title: {
        fontSize: 32,
        topMargin: '1%',
        leftMargin: 'center',   // Center for pie charts
        subtitleGap: 16
      },
      dataDescription: {
        fontSize: 16,
        gap: 8,
        topPosition: '10%',
        chartGap: 20
      },
      chartArea: {
        margins: {
          top: '15%',           // Chart area starts here (dynamically calculated)
          bottom: '15%',        // Chart area ends here
          left: 'center',       // Chart area left margin (centered for pie charts)
          right: 'center'       // Chart area right margin (centered for pie charts)
        }
      },
      legend: {
        fontSize: 16,           // Legend text size
        fontWeight: 400,        // Legend font weight
        color: '#666666',       // Legend color
        alignment: 'center',    // Legend alignment: 'left', 'center', 'right'
        margins: {
          top: '1%',            // Legend position from top
          bottom: 0,            // Distance below legend
          left: '1%',           // Distance from left edge
          right: '1%'           // Distance from right edge
        },
        itemGap: 15,            // Gap between legend items (pixels) - standardized
        chartAreaGap: 20,       // Gap between chart area bottom and legend (pixels)
        ctaGap: 20              // Gap between legend and CTA (pixels)
      },
      cta: {
        fontSize: 20,           // CTA text size
        fontWeight: 400,        // CTA font weight
        color: '#222222',       // CTA color
        alignment: 'center',    // CTA alignment: 'left', 'center', 'right'
        margins: {
          top: 0,               // Distance above CTA
          bottom: '2%',         // CTA position from bottom
          left: '1%',           // Distance from left edge
          right: '1%'           // Distance from right edge
        }
      }
    }
  };

  /**
   * Get base configuration shared by all charts
   */
  static getBaseConfig(): Partial<EChartsOption> {
    return {
      backgroundColor: 'transparent',
      animation: true,
      animationDuration: 800,
      animationEasing: 'cubicOut',
      textStyle: {
        fontFamily: TYPOGRAPHY.fontFamily,
        fontWeight: 'normal'
      }
    };
  }

  /**
   * Get horizontal bar chart grid configuration
   */
  static getHorizontalBarGrid(): any {
    // Calculate chart area top based on data description position + chart gap
    const standards = BaseChartFactory.CHART_TYPE_STANDARDS.horizontalBar;
    const titleTop = 1; // 1% from top
    const titleHeight = standards.title.fontSize;
    const titleSubtitleGap = standards.subtitle.margins.top;
    const subtitleHeight = standards.subtitle.fontSize;
    const dataDescriptionGap = standards.dataDescription.margins.top;
    const chartGap = standards.dataDescription.chartGap;
    
    // Calculate data description position in pixels
    const containerHeight = standards.height.default;
    const subtitleTopPx = (titleTop / 100) * containerHeight + titleHeight + titleSubtitleGap;
    const dataDescriptionTopPx = subtitleTopPx + subtitleHeight + dataDescriptionGap;
    const dataDescriptionHeight = 20; // Approximate height of data description text
    const chartAreaTopPx = dataDescriptionTopPx + dataDescriptionHeight + chartGap;
    
    // Convert to percentage
    const chartAreaTop = `${(chartAreaTopPx / containerHeight) * 100}%`;
    
    return {
      left: standards.chartArea.margins.left,
      right: standards.chartArea.margins.right,
      bottom: standards.chartArea.margins.bottom,
      top: chartAreaTop, // Dynamic calculation based on data description + gap
      containLabel: true
    };
  }

  /**
   * Get vertical bar chart grid configuration
   * Updated to allow x-axis to expand more within the image
   */
  static getVerticalBarGrid(): any {
    // Calculate chart area top based on data description position + chart gap
    const standards = BaseChartFactory.CHART_TYPE_STANDARDS.verticalBar;
    const titleTop = 1; // 1% from top
    const titleHeight = standards.title.fontSize;
    const titleSubtitleGap = standards.subtitle.margins.top;
    const subtitleHeight = standards.subtitle.fontSize;
    const dataDescriptionGap = standards.dataDescription.margins.top;
    const chartGap = standards.dataDescription.chartGap;
    
    // Calculate data description position in pixels
    const containerHeight = standards.height.default;
    const subtitleTopPx = (titleTop / 100) * containerHeight + titleHeight + titleSubtitleGap;
    const dataDescriptionTopPx = subtitleTopPx + subtitleHeight + dataDescriptionGap;
    const dataDescriptionHeight = 20; // Approximate height of data description text
    const chartAreaTopPx = dataDescriptionTopPx + dataDescriptionHeight + chartGap;
    
    // Convert to percentage
    const chartAreaTop = `${(chartAreaTopPx / containerHeight) * 100}%`;
    
    return {
      left: standards.chartArea.margins.left,
      right: standards.chartArea.margins.right,
      bottom: standards.chartArea.margins.bottom,
      top: chartAreaTop, // Dynamic calculation based on data description + gap
      containLabel: true
    };
  }

  /**
   * Get legend configuration with calculated positioning
   */
  static getLegendConfig(orientation: ChartOrientation = ChartOrientation.HORIZONTAL, chartType: 'horizontalBar' | 'verticalBar' | 'line' | 'pie' = 'horizontalBar'): any {
    const standards = BaseChartFactory.CHART_TYPE_STANDARDS[chartType];
    
    // For horizontal and vertical bar charts, use calculated positioning
    if (chartType === 'horizontalBar' || chartType === 'verticalBar') {
      const legendPosition = BaseChartFactory.getLegendPosition(orientation);
      
      return {
        show: true,
        top: legendPosition.top,
        left: standards.legend.alignment === 'center' ? 'center' : standards.legend.margins.left,
        right: standards.legend.alignment === 'center' ? 'center' : standards.legend.margins.right,
        itemGap: standards.legend.itemGap,
        textStyle: {
          fontSize: standards.legend.fontSize,
          fontWeight: standards.legend.fontWeight,
          color: standards.legend.color,
          fontFamily: TYPOGRAPHY.fontFamily
        }
      };
    }
    
    // For line and pie charts, use standard positioning
    return {
      show: true,
      top: standards.legend.margins.top,
      left: standards.legend.alignment === 'center' ? 'center' : standards.legend.margins.left,
      right: standards.legend.alignment === 'center' ? 'center' : standards.legend.margins.right,
      itemGap: standards.legend.itemGap,
      textStyle: {
        fontSize: standards.legend.fontSize,
        fontWeight: standards.legend.fontWeight,
        color: standards.legend.color,
        fontFamily: TYPOGRAPHY.fontFamily
      }
    };
  }

  /**
   * Get standard tooltip configuration
   */
  static getTooltipConfig(): any {
    return {
      trigger: 'axis',
      backgroundColor: 'rgba(255, 255, 255, 0.98)',
      borderColor: this.PREMIUM_COLORS.border,
      borderWidth: 1,
      borderRadius: 12,
      padding: [16, 20],
      textStyle: {
        color: this.PREMIUM_COLORS.text,
        fontSize: 14, // Moderate increase for better readability
        fontFamily: TYPOGRAPHY.fontFamily
      },
      extraCssText: 'box-shadow: 0 8px 32px rgba(0,0,0,0.12);'
    };
  }

  /**
   * Utility function to safely extract tooltip parameters
   * Handles both single item and axis trigger (array of items) consistently
   */
  static extractTooltipParams(params: any): any[] {
    return Array.isArray(params) ? params : [params];
  }


  /**
   * Get legend configuration for vertical bar charts (bottom positioned)
   */
  static getVerticalBarLegendConfig(): any {
    return {
      bottom: '6%',
      left: 'center',
      itemGap: 30,
      textStyle: {
        fontSize: 16, // Moderate increase for better readability
        fontWeight: 600,
        color: this.PREMIUM_COLORS.textSecondary,
        fontFamily: TYPOGRAPHY.fontFamily
      }
    };
  }

  /**
   * Get height configuration for a specific chart type
   */
  static getChartHeightConfig(chartType: 'horizontalBar' | 'verticalBar' | 'line' | 'pie'): { default: number; min: number; max: number; responsive: boolean } {
    return BaseChartFactory.CHART_TYPE_STANDARDS[chartType].height;
  }

  /**
   * Calculate legend position based on chart area bottom and spacing
   */
  static getLegendPosition(orientation: ChartOrientation = ChartOrientation.HORIZONTAL): { top: string; bottom: string } {
    const standards = orientation === ChartOrientation.HORIZONTAL 
      ? BaseChartFactory.CHART_TYPE_STANDARDS.horizontalBar
      : BaseChartFactory.CHART_TYPE_STANDARDS.verticalBar;
    
    // Calculate chart area bottom position
    const containerHeight = standards.height.default;
    const chartAreaBottomPx = containerHeight - (parseFloat(standards.chartArea.margins.bottom.replace('%', '')) / 100) * containerHeight;
    
    // Add gap between chart area and legend
    const legendTopPx = chartAreaBottomPx + standards.legend.chartAreaGap;
    const legendTop = `${(legendTopPx / containerHeight) * 100}%`;
    
    // Calculate CTA position from legend (for bottom positioning)
    const legendHeight = 20; // Approximate legend height
    const ctaTopPx = legendTopPx + legendHeight + standards.legend.ctaGap;
    const ctaBottomPx = containerHeight - ctaTopPx;
    const ctaBottom = `${(ctaBottomPx / containerHeight) * 100}%`;
    
    return {
      top: legendTop,
      bottom: ctaBottom
    };
  }

  /**
   * Get data description line graphic (Carta-style)
   * Goes between subtitle and chart canvas - positioned with proper spacing
   */
  static getDataDescriptionGraphic(description: string, orientation: ChartOrientation = ChartOrientation.HORIZONTAL): any[] {
    // Use chart-type-specific standards based on orientation
    const standards = orientation === ChartOrientation.HORIZONTAL 
      ? BaseChartFactory.CHART_TYPE_STANDARDS.horizontalBar
      : BaseChartFactory.CHART_TYPE_STANDARDS.verticalBar;
    
    // Calculate position based on subtitle position + gap
    const titleTop = 1; // 1% from top
    const titleHeight = standards.title.fontSize;
    const titleSubtitleGap = standards.subtitle.margins.top;
    const subtitleHeight = standards.subtitle.fontSize;
    const dataDescriptionGap = standards.dataDescription.margins.top;
    const chartGap = standards.dataDescription.chartGap;
    
    // Calculate subtitle position in pixels using chart height configuration
    const containerHeight = standards.height.default;
    const subtitleTopPx = (titleTop / 100) * containerHeight + titleHeight + titleSubtitleGap;
    const dataDescriptionTopPx = subtitleTopPx + subtitleHeight + dataDescriptionGap;
    
    // Convert back to percentage for ECharts
    const topPosition = `${(dataDescriptionTopPx / containerHeight) * 100}%`;
    const linePosition = `${((dataDescriptionTopPx + 20) / containerHeight) * 100}%`; // 20px below text
    
    
    return [
      // Description text - positioned below subtitle with breathing room
      {
        type: 'text',
        left: '1%', // Match grid and title margins
        top: topPosition,
        style: {
          text: description,
          fontSize: 16,
          fontWeight: '400',
          fontFamily: TYPOGRAPHY.fontFamily,
          fill: this.PREMIUM_COLORS.textSecondary,
          lineHeight: 17
        },
        z: 100
      }
    ];
  }

  /**
   * Get CTA footer graphic (Carta-style)
   * "Subscribe for more cybersecurity market intelligence at ReturnOnSecurity.com"
   * ReturnOnSecurity.com is bold and electric purple
   */
  static getCTAGraphic(orientation: ChartOrientation = ChartOrientation.HORIZONTAL): any[] {
    // Use chart-type-specific standards based on orientation
    const standards = orientation === ChartOrientation.HORIZONTAL 
      ? BaseChartFactory.CHART_TYPE_STANDARDS.horizontalBar
      : BaseChartFactory.CHART_TYPE_STANDARDS.verticalBar;
    
    // Calculate CTA position based on legend spacing
    const legendPosition = BaseChartFactory.getLegendPosition(orientation);
    const separatorBottom = legendPosition.bottom;
    const textBottom = legendPosition.bottom;
    
    return [
      // Separator line above CTA
      {
        type: 'line',
        left: '5%',
        right: '5%',
        bottom: separatorBottom,
        shape: {
          x1: 0,
          y1: 0,
          x2: 1,
          y2: 0
        },
        style: {
          stroke: this.PREMIUM_COLORS.border,
          lineWidth: 1
        }
      },
      // CTA text using rich text formatting to style parts differently
      {
        type: 'text',
        left: 'center',
        bottom: textBottom,
        style: {
          text: '{normal|Subscribe for more cybersecurity market intelligence at }{purple|ReturnOnSecurity.com}',
          fontSize: standards.cta.fontSize,
          fontFamily: TYPOGRAPHY.fontFamily,
          textAlign: 'center',
          rich: {
            normal: {
              fontSize: standards.cta.fontSize,
              fontWeight: standards.cta.fontWeight,
              fill: standards.cta.color
            },
            purple: {
              fontSize: standards.cta.fontSize,
              fontWeight: 700,
              fill: this.PREMIUM_COLORS.primary // Electric purple #633FF3
            }
          }
        }
      }
    ];
  }

  /**
   * Get chart border and footer graphic
   */
  static getChartBorder(orientation: ChartOrientation, dataDescription?: string): any[] {
    const graphics: any[] = [];

    // Add data description if provided
    if (dataDescription) {
      graphics.push(...this.getDataDescriptionGraphic(dataDescription, orientation));
    }

    // Add CTA footer
    graphics.push(...this.getCTAGraphic(orientation));

    return graphics;
  }

  /**
   * Calculate dynamic chart height based on data
   */
  static calculateChartHeight(dataLength: number, minHeight: number = 400, maxHeight: number = 800): number {
    const baseHeight = Math.max(dataLength * 60, minHeight);
    return Math.min(baseHeight, maxHeight);
  }

  /**
   * Calculate dynamic grid configuration based on data and chart type
   * This replicates the calculateDynamicGrid functionality from ChartConfigurationEngine
   */
  static calculateDynamicGrid(data: any[], chartType: string): any {
    const dataLength = data.length;

    if (chartType === 'country-funding' || chartType === 'product-category' || chartType === 'acquisition-category') {
      // All horizontal bar charts need consistent spacing for category names
      return {
        left: '1%', // Match other chart margins
        right: '1%', // Match left margin - data labels handled within content area
        bottom: '12%',
        top: BaseChartFactory.CHART_TYPE_STANDARDS.horizontalBar.chartArea.margins.top,
        containLabel: true
      };
    }

    // Default grid for other chart types
    return this.getHorizontalBarGrid();
  }

  /**
   * Format currency values for display
   */
  static formatCurrency(value: number): string {
    if (value >= 1000000000) {
      const billions = value / 1000000000;
      // Use 2 decimals for values >= $1B to avoid rounding confusion
      return `$${billions.toFixed(2)}B`;
    }
    if (value >= 1000000) return `$${(value / 1000000).toFixed(0)}M`;
    if (value >= 1000) return `$${(value / 1000).toFixed(0)}K`;
    return `$${value}`;
  }

  /**
   * Calculate nice axis scale for clean intervals with easy-to-read numbers
   */
  static calculateNiceAxisScale(maxValue: number): { max: number; interval: number } {
    // Create more intervals (8-10) for better readability
    const targetIntervals = 8;
    const niceMax = Math.ceil(maxValue * 1.1);
    
    // Special case for very small values (like counts)
    if (maxValue <= 10) {
      const interval = Math.ceil(niceMax / targetIntervals);
      return { max: niceMax, interval: Math.max(1, interval) };
    }
    
    // Calculate a nice interval that results in easy-to-read numbers
    let interval = niceMax / targetIntervals;
    
    // Round to nice numbers (1, 2, 5, 10, 20, 50, 100, 200, 500, 1000, etc.)
    const magnitude = Math.pow(10, Math.floor(Math.log10(interval)));
    const normalized = interval / magnitude;
    
    let niceInterval;
    if (normalized <= 1) {
      niceInterval = 1 * magnitude;
    } else if (normalized <= 2) {
      niceInterval = 2 * magnitude;
    } else if (normalized <= 5) {
      niceInterval = 5 * magnitude;
    } else {
      niceInterval = 10 * magnitude;
    }
    
    // Check if this gives us too few intervals - if so, use a smaller interval
    const actualIntervals = Math.ceil(niceMax / niceInterval);
    if (actualIntervals < 6) {
      // Use a smaller interval to get closer to 8 intervals
      const smallerInterval = niceInterval / 2;
      const adjustedMax = Math.ceil(niceMax / smallerInterval) * smallerInterval;
      return { max: adjustedMax, interval: smallerInterval };
    }
    
    // Adjust max to be a multiple of the nice interval
    const niceMaxAdjusted = Math.ceil(niceMax / niceInterval) * niceInterval;
    
    return { max: niceMaxAdjusted, interval: niceInterval };
  }

  /**
   * Generate dynamic titles and subtitles based on data analysis
   * Preserves all the sophisticated title generation from the original system
   * Now unified and context-aware across all time domains
   */
  static generateDynamicTitles(config: ChartConfig, processedData: any): { title: string; subtitle: string } {
    // CRITICAL: If a non-empty title was explicitly provided (e.g., custom title from user),
    // use it instead of dynamic generation, even if enableDynamicTitles is true
    if (config.title && config.title.trim() !== '') {
      return { title: config.title, subtitle: config.subtitle || '' };
    }

    if (!config.options?.enableDynamicTitles) {
      return { title: config.title, subtitle: config.subtitle || '' };
    }

    const { series, categories } = processedData;
    const isMA = config.options.isMA || false;
    const isTTM = config.options.isTTM || false;
    const chartContext = config.options.chartContext || 'default';
    const timeframe = config.options.timeframe || 'custom';
    const timeGrouping = config.options.timeGrouping || 'weeks';
    const timeDomain = config.options.timeDomain;

    // Generate time-aware context
    const timeContext = this.generateTimeContext(timeframe, timeGrouping, timeDomain);

    // Handle different chart types with unified time context
    switch (config.type) {
      case ChartType.COUNTRY_FUNDING:
        return this.generateCountryFundingTitles(config, processedData, chartContext, timeContext);
      
      case ChartType.ACQUISITION_CATEGORY:
      case ChartType.PRODUCT_CATEGORY:
        return this.generateCategoryTitles(config, processedData, chartContext, timeContext);
      
      case ChartType.YEAR_OVER_YEAR:
        return this.generateYearOverYearTitles(config, series, isMA, isTTM, timeContext);
      
      default:
        return { title: config.title, subtitle: config.subtitle || '' };
    }
  }

  /**
   * Generate unified time context for all chart types
   * Handles all time domains: weekly, monthly, quarterly, TTM, YTD, custom periods
   */
  private static generateTimeContext(
    timeframe: string, 
    timeGrouping: string, 
    timeDomain?: { startDate?: string; endDate?: string; periodLabel?: string; weekOffset?: number }
  ): {
    periodLabel: string;
    timeDescription: string;
    isRealtime: boolean;
    isHistorical: boolean;
    isProjected: boolean;
    granularity: string;
  } {
    const now = new Date();
    const currentYear = now.getFullYear();
    const currentWeek = this.getWeekNumber(now);
    
    // Determine period label and description
    let periodLabel = timeDomain?.periodLabel || '';
    let timeDescription = '';
    let isRealtime = false;
    let isHistorical = false;
    let isProjected = false;
    let granularity = timeGrouping;

    switch (timeframe) {
      case '1w':
        periodLabel = `Week ${currentWeek}, ${currentYear}`;
        timeDescription = 'Current Week';
        isRealtime = true;
        granularity = 'weeks';
        break;
        
      case '2w':
        periodLabel = `Weeks ${currentWeek - 1}-${currentWeek}, ${currentYear}`;
        timeDescription = 'Last 2 Weeks';
        isRealtime = true;
        granularity = 'weeks';
        break;
        
      case '4w':
        periodLabel = `Weeks ${currentWeek - 3}-${currentWeek}, ${currentYear}`;
        timeDescription = 'Last 4 Weeks';
        isRealtime = true;
        granularity = 'weeks';
        break;
        
      case '12w':
        periodLabel = `Weeks ${currentWeek - 11}-${currentWeek}, ${currentYear}`;
        timeDescription = 'Last 12 Weeks';
        isRealtime = true;
        granularity = 'weeks';
        break;
        
      case '6m':
        const sixMonthsAgo = new Date(now.getFullYear(), now.getMonth() - 6, now.getDate());
        periodLabel = `${sixMonthsAgo.toLocaleDateString('en-US', { month: 'short', year: 'numeric' })} - ${now.toLocaleDateString('en-US', { month: 'short', year: 'numeric' })}`;
        timeDescription = 'Last 6 Months';
        isRealtime = true;
        granularity = 'months';
        break;
        
      case 'ytd':
        periodLabel = `January - ${now.toLocaleDateString('en-US', { month: 'long' })}, ${currentYear}`;
        timeDescription = 'Year to Date';
        isRealtime = true;
        granularity = 'months';
        break;
        
      case 'ttm':
        const oneYearAgo = new Date(now.getFullYear() - 1, now.getMonth(), now.getDate());
        periodLabel = `${oneYearAgo.toLocaleDateString('en-US', { month: 'short', year: 'numeric' })} - ${now.toLocaleDateString('en-US', { month: 'short', year: 'numeric' })}`;
        timeDescription = 'Trailing 12 Months';
        isRealtime = true;
        granularity = 'months';
        break;
        
      case 'custom':
        if (timeDomain?.startDate && timeDomain?.endDate) {
          const startDate = new Date(timeDomain.startDate);
          const endDate = new Date(timeDomain.endDate);
          periodLabel = `${startDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - ${endDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`;
          timeDescription = periodLabel;
          isRealtime = endDate >= now;
          isHistorical = endDate < now;
        } else {
          periodLabel = 'Custom Range';
          timeDescription = 'Custom Range';
        }
        break;
        
      default:
        periodLabel = timeDomain?.periodLabel || 'Current Period';
        timeDescription = 'Current Period';
        isRealtime = true;
    }

    return {
      periodLabel,
      timeDescription,
      isRealtime,
      isHistorical,
      isProjected,
      granularity
    };
  }

  /**
   * Get week number for a given date
   */
  private static getWeekNumber(date: Date): number {
    const firstDayOfYear = new Date(date.getFullYear(), 0, 1);
    const pastDaysOfYear = (date.getTime() - firstDayOfYear.getTime()) / 86400000;
    return Math.ceil((pastDaysOfYear + firstDayOfYear.getDay() + 1) / 7);
  }

  /**
   * Generate TitleContext from chart configuration and data
   * Extracts week ranges and other context for data description generation
   */
  static generateTitleContext(config: ChartConfig, data?: any): TitleContext {
    const now = new Date();
    const currentYear = now.getFullYear();
    const weekOffset = config.options?.weekOffset || 0;

    const timeframe = config.options?.timeframe || '12w';
    const timeGrouping = config.options?.timeGrouping || 'weeks';

    // Calculate proper dataPoints from timeframe, not data length
    // For horizontal bar charts, data is an array of categories (countries/products), not time periods
    let dataPoints = 12; // default
    if (timeframe.endsWith('w')) {
      // Extract week count from timeframe (e.g., '12w' -> 12)
      dataPoints = parseInt(timeframe.replace('w', ''));
    } else if (timeframe.endsWith('m')) {
      // Extract month count from timeframe (e.g., '6m' -> 6)
      dataPoints = parseInt(timeframe.replace('m', ''));
    } else if (timeframe.endsWith('q')) {
      // Extract quarter count from timeframe (e.g., '4q' -> 4)
      dataPoints = parseInt(timeframe.replace('q', ''));
    }

    // Determine chart type and whether it's M&A
    let chartType: 'funding' | 'ma' | 'category' | 'country' = 'funding';
    let isMA = config.options?.isMA || false;

    // Check chart type enum first
    if (config.type === ChartType.ACQUISITION_CATEGORY) {
      chartType = 'category';
      isMA = true; // ACQUISITION charts are always M&A
    } else if (config.type === ChartType.PRODUCT_CATEGORY) {
      chartType = 'category';
      isMA = false; // PRODUCT charts are funding
    } else if (config.type === ChartType.COUNTRY_FUNDING) {
      chartType = 'country';
    } else if (config.type.toString().includes('acquisition') || isMA) {
      chartType = 'ma';
    }

    // Extract week ranges from actual chart data (not calculated)
    let startWeek: number | undefined;
    let endWeek: number | undefined;
    if (timeGrouping === 'weeks' && timeframe.endsWith('w')) {
      // Try to extract week numbers from various data structures
      let weekLabels: string[] = [];
      
      // For vertical charts (V2ChartData), check x_axis
      if (data && data.x_axis && Array.isArray(data.x_axis)) {
        weekLabels = data.x_axis;
      }
      // For horizontal charts, check if data has week information
      else if (data && Array.isArray(data) && data.length > 0) {
        // Check if data items have week information
        const firstItem = data[0];
        if (firstItem && firstItem.week) {
          weekLabels = data.map((item: any) => item.week).filter(Boolean);
        }
      }
      
      if (weekLabels.length > 0) {
        // Extract week numbers from labels like "W29", "W30", etc.
        const weekNumbers = weekLabels.map((label: string) => {
          const match = label.match(/W(\d+)/);
          return match ? parseInt(match[1]) : null;
        }).filter(num => num !== null) as number[];
        
        if (weekNumbers.length > 0) {
          startWeek = Math.min(...weekNumbers);
          endWeek = Math.max(...weekNumbers);
        }
      }
    }

    return {
      timeframe,
      timeGrouping,
      dataPoints,
      year: currentYear,
      chartType,
      startWeek,
      endWeek,
      isMA,
      isTTM: config.options?.isTTM
    };
  }


  private static generateCountryFundingTitles(
    config: ChartConfig,
    data: any,
    context: string,
    timeContext: any
  ): { title: string; subtitle: string } {
    const { categories, values, rawCountries } = data;
    const totalFunding = values.reduce((sum: number, val: number) => sum + val, 0);
    const topCountryShare = totalFunding > 0 ? ((values[0] / totalFunding) * 100) : 0;
    const totalDealCount = data.dealCounts?.reduce((sum: number, count: number) => sum + count, 0) || 0;

    // Use raw country data for clean title (single space between name and flag, only if flag exists)
    const topCountry = rawCountries && rawCountries[0]
      ? rawCountries[0].flag
        ? `${rawCountries[0].name} ${rawCountries[0].flag}` // Include flag only if it exists
        : rawCountries[0].name // Just the name if no flag
      : (categories && categories[0]) ? categories[0].replace(/\s\s+/g, ' ') : 'Unknown';

    // Use existing generateTitleContext function for proper week range calculation
    const baseTitleContext = BaseChartFactory.generateTitleContext(config, data);
    
    // Build TitleContext for AI-driven story generation with proper week ranges
    const titleContext: TitleContext = {
      ...baseTitleContext,
      topCategory: topCountry,
      topCategoryShare: topCountryShare,
      totalCount: totalFunding,
      categoryCount: categories.length,
      chartType: 'country'
    };

    // Generate story-driven title and AI-driven narrative subtitle using TitleUtils
    const title = TitleUtils.generateStoryTitle(titleContext);
    const subtitle = TitleUtils.generateStorySubtitle(titleContext);

    return { title, subtitle };
  }

  private static generateCategoryTitles(
    config: ChartConfig,
    data: any,
    context: string,
    timeContext: any
  ): { title: string; subtitle: string } {
    const { categories, values } = data;
    const totalValue = values.reduce((sum: number, val: number) => sum + val, 0);

    // Calculate top categories with shares for AI-driven narrative generation
    const topCategories = categories.slice(0, Math.min(5, categories.length)).map((name: string, idx: number) => ({
      name,
      value: values[idx],
      share: totalValue > 0 ? (values[idx] / totalValue) * 100 : 0
    }));

    const topCategory = categories[0];
    const topCategoryShare = totalValue > 0 ? (values[0] / totalValue) * 100 : 0;
    const secondCategory = categories.length > 1 ? categories[1] : undefined;
    const secondCategoryShare = categories.length > 1 && totalValue > 0 ? (values[1] / totalValue) * 100 : undefined;
    const thirdCategory = categories.length > 2 ? categories[2] : undefined;
    const thirdCategoryShare = categories.length > 2 && totalValue > 0 ? (values[2] / totalValue) * 100 : undefined;

    // Use existing generateTitleContext function for proper week range calculation
    const baseTitleContext = BaseChartFactory.generateTitleContext(config, data);
    
    // Build TitleContext for AI-driven story generation with proper week ranges
    const titleContext: TitleContext = {
      ...baseTitleContext,
      topCategory,
      topCategoryShare,
      secondCategory,
      secondCategoryShare,
      thirdCategory,
      thirdCategoryShare,
      topCategories,
      totalCount: totalValue,
      categoryCount: categories.length,
      chartType: 'category',
      isMA: config.type === ChartType.ACQUISITION_CATEGORY || config.options?.isMA
    };

    // Generate story-driven title and AI-driven narrative subtitle using TitleUtils
    const title = TitleUtils.generateStoryTitle(titleContext);
    const subtitle = TitleUtils.generateStorySubtitle(titleContext);

    return { title, subtitle };
  }

  private static generateYearOverYearTitles(
    config: ChartConfig,
    series: any[],
    isMA: boolean,
    isTTM: boolean,
    timeContext: any
  ): { title: string; subtitle: string } {
    if (!series || series.length === 0) {
      return { title: config.title, subtitle: config.subtitle || '' };
    }

    const visibleSeries = series.filter(s => s.data && s.data.length > 0);
    const visibleTotals = visibleSeries.map(s => s.data.reduce((sum: number, val: number) => sum + val, 0));
    const totalValue = visibleTotals.reduce((sum: number, val: number) => sum + val, 0);

    // Calculate year-over-year growth rate for story-driven titles
    let growthRate: number | undefined;
    let currentYearTotal: number | undefined;

    if (visibleSeries.length >= 2 && visibleTotals.length >= 2) {
      const previousTotal = visibleTotals[visibleTotals.length - 2];
      currentYearTotal = visibleTotals[visibleTotals.length - 1];
      if (previousTotal > 0) {
        growthRate = ((currentYearTotal - previousTotal) / previousTotal) * 100;
      }
    } else if (visibleSeries.length === 1) {
      currentYearTotal = visibleTotals[0];
    }


    // Build TitleContext for story-driven title generation
    const titleContext: TitleContext = {
      timeframe: config.options?.timeframe || '12w',
      timeGrouping: config.options?.timeGrouping || 'weeks',
      isMA: isMA,
      isTTM: isTTM,
      totalValue: currentYearTotal || 0, // Use current year total, not sum of all years
      totalCount: isMA ? (currentYearTotal || 0) : undefined, // For MA, currentYearTotal IS the count
      chartType: isMA ? 'ma' : 'funding',
      growthRate: growthRate,
      trend: growthRate !== undefined ? TitleUtils.calculateTrend(growthRate) : undefined,
      periodLabel: timeContext.periodLabel
    };

    // Use TitleUtils for consistent story-driven titles
    const title = TitleUtils.generateStoryTitle(titleContext);
    const subtitle = TitleUtils.generateStorySubtitle(titleContext);

    return { title, subtitle };
  }
}

// ============================================================================
// CHART TYPE FACTORY
// ============================================================================

export class ChartTypeFactory {
  /**
   * Create horizontal bar chart configuration
   */
  static createHorizontalBarChart(config: ChartConfig): EChartsOption {
    const baseConfig = BaseChartFactory.getBaseConfig();
    const gridConfig = BaseChartFactory.getHorizontalBarGrid();
    const tooltipConfig = BaseChartFactory.getTooltipConfig();
    const legendConfig = BaseChartFactory.getLegendConfig(ChartOrientation.HORIZONTAL, 'horizontalBar');

    // Process data based on type
    const { categories, values, maxValue, dealCounts, rawCountries } = this.processHorizontalBarData(config);

    // Generate dynamic titles if enabled
    const processedData = { categories, values, maxValue, dealCounts, rawCountries };
    const { title: dynamicTitle, subtitle: dynamicSubtitle } = config.options?.enableDynamicTitles
      ? BaseChartFactory.generateDynamicTitles(config, processedData)
      : { title: config.title, subtitle: config.subtitle || '' };

    // Generate data description (Carta-style)
    let dataDescription: string;
    if (config.options?.dataDescription) {
      // Use explicit description if provided
      dataDescription = config.options.dataDescription;
    } else {
      // Auto-generate context if not provided
      const titleContext = config.options?.titleContext || BaseChartFactory.generateTitleContext(config, config.data);
      // Generate from context using TitleUtils
      dataDescription = TitleUtils.generateDataDescription(titleContext);
    }

    const graphicConfig = BaseChartFactory.getChartBorder(ChartOrientation.HORIZONTAL, dataDescription);

    return {
      ...baseConfig,
      title: {
        text: dynamicTitle,
        subtext: dynamicSubtitle,
        left: BaseChartFactory.CHART_TYPE_STANDARDS.horizontalBar.title.margins.left,
        top: BaseChartFactory.CHART_TYPE_STANDARDS.horizontalBar.title.margins.top,
        padding: 0,
        itemGap: BaseChartFactory.CHART_TYPE_STANDARDS.horizontalBar.subtitle.margins.top,
        textStyle: {
          fontSize: BaseChartFactory.CHART_TYPE_STANDARDS.horizontalBar.title.fontSize,
          fontWeight: 700,
          color: BaseChartFactory.PREMIUM_COLORS.text,
          fontFamily: TYPOGRAPHY.fontFamily,
          padding: 0
        },
        subtextStyle: {
          fontSize: BaseChartFactory.CHART_TYPE_STANDARDS.horizontalBar.subtitle.fontSize,
          fontWeight: 500,
          color: BaseChartFactory.PREMIUM_COLORS.textSecondary,
          fontFamily: TYPOGRAPHY.fontFamily,
          padding: 0
        }
      },
      grid: gridConfig,
      tooltip: {
        ...tooltipConfig,
        trigger: 'axis',
        axisPointer: {
          type: 'shadow',
          shadowStyle: {
            color: 'rgba(99, 63, 243, 0.08)'
          }
        },
        formatter: this.createHorizontalBarTooltipFormatter(config.dataType, config.data)
      },
      legend: legendConfig,
      xAxis: this.createHorizontalBarXAxis(maxValue, config.dataType),
      yAxis: this.createHorizontalBarYAxis(categories),
      series: [{
        type: 'bar',
        data: values,
        barCategoryGap: '15%',  // Spacing between different categories (bars)
        itemStyle: {
          color: BaseChartFactory.PREMIUM_COLORS.primary,
          borderRadius: [0, 8, 8, 0],
          shadowColor: 'rgba(99, 63, 243, 0.1)',
          shadowBlur: 8,
          shadowOffsetY: 2
        },
        label: {
          show: config.options?.showDataLabels !== false,
          position: 'right',
          formatter: (params: any) => {
            if (config.dataType === DataType.CURRENCY) {
              return BaseChartFactory.formatCurrency(params.value);
            }
            return params.value.toString();
          },
          fontSize: 16, // Moderate increase for better readability
          fontWeight: 600,
          color: BaseChartFactory.PREMIUM_COLORS.primary,
          distance: 8
        },
        barMaxWidth: BaseChartFactory.CHART_TYPE_STANDARDS.horizontalBar.bar.height,
        animationDuration: 800,
        animationDelay: (idx: number) => idx * 100
      }],
      graphic: graphicConfig
    };
  }

  /**
   * Create premium funding chart (YoY line chart with growth analysis)
   */
  static createPremiumFundingChart(config: ChartConfig): EChartsOption {
    const baseConfig = BaseChartFactory.getBaseConfig();
    const gridConfig = BaseChartFactory.getVerticalBarGrid();
    const tooltipConfig = BaseChartFactory.getTooltipConfig();
    const legendConfig = BaseChartFactory.getLegendConfig(ChartOrientation.VERTICAL, 'verticalBar');

    // Process premium funding data
    const { weeks, weeklyComparison, currentYear, previousYear } = this.processPremiumFundingData(config);

    // Generate dynamic titles
    const processedData = { weeks, weeklyComparison, currentYear, previousYear };
    const { title: dynamicTitle, subtitle: dynamicSubtitle } = config.options?.enableDynamicTitles
      ? BaseChartFactory.generateDynamicTitles(config, processedData)
      : { title: config.title, subtitle: config.subtitle || '' };

    // Generate data description (Carta-style)
    const dataDescription = config.options?.dataDescription ||
      `Return on Security funding activity over the ${config.options?.timeframe || 'selected period'}`;

    const graphicConfig = BaseChartFactory.getChartBorder(ChartOrientation.VERTICAL, dataDescription);

    return {
      ...baseConfig,
      title: {
        text: dynamicTitle,
        subtext: dynamicSubtitle,
        left: BaseChartFactory.CHART_TYPE_STANDARDS.horizontalBar.title.margins.left,
        top: BaseChartFactory.CHART_TYPE_STANDARDS.horizontalBar.title.margins.top,
        padding: 0,
        itemGap: BaseChartFactory.CHART_TYPE_STANDARDS.horizontalBar.subtitle.margins.top,
        textStyle: {
          fontSize: BaseChartFactory.CHART_TYPE_STANDARDS.horizontalBar.title.fontSize,
          fontWeight: 700,
          color: BaseChartFactory.PREMIUM_COLORS.text,
          fontFamily: TYPOGRAPHY.fontFamily,
          padding: 0
        },
        subtextStyle: {
          fontSize: BaseChartFactory.CHART_TYPE_STANDARDS.horizontalBar.subtitle.fontSize,
          fontWeight: 500,
          color: BaseChartFactory.PREMIUM_COLORS.textSecondary,
          fontFamily: TYPOGRAPHY.fontFamily,
          padding: 0
        }
      },
      grid: gridConfig,
      tooltip: {
        ...tooltipConfig,
        formatter: this.createPremiumFundingTooltipFormatter(weeklyComparison)
      },
      legend: legendConfig,
      xAxis: {
        type: 'category',
        data: weeks,
        axisLabel: {
          formatter: 'W{value}',
          interval: Math.ceil(weeks.length / 8),
          fontSize: 16,
          fontWeight: 600,
          color: BaseChartFactory.PREMIUM_COLORS.textSecondary,
          fontFamily: TYPOGRAPHY.fontFamily
        },
        axisLine: {
          lineStyle: { color: '#e5e7eb', width: 2 } // Carta-style: clearly visible axis line
        },
        axisTick: { show: false }
      },
      yAxis: {
        type: 'value',
        name: 'Funding Volume',
        axisLabel: {
          formatter: (value: number) => {
            if (value >= 1000) return `$${(value / 1000).toFixed(1)}B`;
            if (value >= 1) return `$${value.toFixed(0)}M`;
            return `$${(value * 1000).toFixed(0)}K`;
          },
          fontSize: 16,
          fontWeight: 600,
          color: BaseChartFactory.PREMIUM_COLORS.textSecondary,
          fontFamily: TYPOGRAPHY.fontFamily
        },
        axisLine: {
          lineStyle: { color: '#e5e7eb', width: 2 } // Carta-style: clearly visible axis line
        },
        splitLine: {
          show: true,
          lineStyle: { color: BaseChartFactory.PREMIUM_COLORS.gridLines }
        }
      },
      series: [
        {
          name: 'Current Period',
          type: 'line',
          data: weeks.map(week => weeklyComparison[week]?.current || 0),
          lineStyle: {
            color: BaseChartFactory.PREMIUM_COLORS.primary,
            width: 3,
            shadowColor: 'rgba(139, 92, 246, 0.3)',
            shadowBlur: 8,
            shadowOffsetY: 2
          },
          itemStyle: {
            color: BaseChartFactory.PREMIUM_COLORS.primary,
            borderWidth: 2,
            borderColor: '#FFFFFF',
            shadowColor: 'rgba(139, 92, 246, 0.3)',
            shadowBlur: 8,
            shadowOffsetY: 2
          },
          areaStyle: {
            color: {
              type: 'linear',
              x: 0, y: 0, x2: 0, y2: 1,
              colorStops: [
                { offset: 0, color: 'rgba(99, 63, 243, 0.15)' },
                { offset: 0.6, color: 'rgba(99, 63, 243, 0.06)' },
                { offset: 1, color: 'rgba(99, 63, 243, 0.01)' }
              ]
            }
          },
          symbol: 'circle',
          symbolSize: 6,
          emphasis: {
            itemStyle: {
              borderWidth: 3,
              shadowBlur: 12,
              shadowOffsetY: 4
            }
          }
        },
        {
          name: 'Previous Period',
          type: 'line',
          data: weeks.map(week => weeklyComparison[week]?.previous || 0),
          lineStyle: {
            color: BaseChartFactory.PREMIUM_COLORS.secondary,
            width: 2,
            type: 'dashed'
          },
          itemStyle: {
            color: BaseChartFactory.PREMIUM_COLORS.secondary,
            borderWidth: 2,
            borderColor: '#FFFFFF'
          },
          symbol: 'circle',
          symbolSize: 4,
          emphasis: {
            itemStyle: {
              borderWidth: 3,
              shadowBlur: 8,
              shadowOffsetY: 2
            }
          }
        }
      ],
      graphic: graphicConfig,
      animation: true,
      animationDuration: 1000,
      animationEasing: 'cubicOut'
    };
  }

  /**
   * Create vertical bar chart configuration
   */
  static createVerticalBarChart(config: ChartConfig): EChartsOption {
    const baseConfig = BaseChartFactory.getBaseConfig();
    const gridConfig = BaseChartFactory.getVerticalBarGrid();
    const tooltipConfig = BaseChartFactory.getTooltipConfig();
    const legendConfig = BaseChartFactory.getLegendConfig(ChartOrientation.VERTICAL, 'verticalBar');

    // Process data based on type
    const { categories, values, series } = this.processVerticalBarData(config);

    // Detect if this is a comparison (stacked) chart
    const isComparisonChart = config.type === ChartType.COMPARISON;

    // Generate dynamic titles if enabled
    const processedData = { categories, values, series };
    const { title: dynamicTitle, subtitle: dynamicSubtitle} = config.options?.enableDynamicTitles
      ? BaseChartFactory.generateDynamicTitles(config, processedData)
      : { title: config.title, subtitle: config.subtitle || '' };


    // Generate data description (Carta-style)
    let dataDescription: string;
    if (config.options?.dataDescription) {
      // Use explicit description if provided
      dataDescription = config.options.dataDescription;
    } else {
      // Auto-generate context if not provided
      const titleContext = config.options?.titleContext || BaseChartFactory.generateTitleContext(config, config.data);
      // Generate from context using TitleUtils
      dataDescription = TitleUtils.generateDataDescription(titleContext);
    }

    const graphicConfig = BaseChartFactory.getChartBorder(ChartOrientation.VERTICAL, dataDescription);

    // For comparison charts, calculate max stacked value instead of max single value
    let maxValue = 0;
    if (isComparisonChart) {
      // Calculate max stacked height per category for comparison charts
      if (series.length > 0 && series[0].data) {
        const categoryCount = series[0].data.length;
        for (let i = 0; i < categoryCount; i++) {
          let stackedValue = 0;
          series.forEach(s => {
            if (s.data && s.data[i]) {
              stackedValue += s.data[i];
            }
          });
          maxValue = Math.max(maxValue, stackedValue);
        }
      }
    } else {
      // Calculate max single value for regular charts
      series.forEach(s => {
        if (s.data && Array.isArray(s.data)) {
          const seriesMax = Math.max(...s.data);
          maxValue = Math.max(maxValue, seriesMax);
        }
      });
    }

    return {
      ...baseConfig,
      title: {
        text: dynamicTitle,
        subtext: dynamicSubtitle,
        left: BaseChartFactory.CHART_TYPE_STANDARDS.verticalBar.title.margins.left,
        top: BaseChartFactory.CHART_TYPE_STANDARDS.verticalBar.title.margins.top,
        padding: 0,
        itemGap: BaseChartFactory.CHART_TYPE_STANDARDS.verticalBar.subtitle.margins.top,
        textStyle: {
          fontSize: BaseChartFactory.CHART_TYPE_STANDARDS.verticalBar.title.fontSize,
          fontWeight: 700,
          color: BaseChartFactory.PREMIUM_COLORS.text,
          fontFamily: TYPOGRAPHY.fontFamily,
          padding: 0
        },
        subtextStyle: {
          fontSize: BaseChartFactory.CHART_TYPE_STANDARDS.verticalBar.subtitle.fontSize,
          fontWeight: 500,
          color: BaseChartFactory.PREMIUM_COLORS.textSecondary,
          fontFamily: TYPOGRAPHY.fontFamily,
          padding: 0
        }
      },
      grid: gridConfig,
      tooltip: {
        ...tooltipConfig,
        trigger: 'axis',
        axisPointer: {
          type: 'shadow'
        },
        formatter: this.createVerticalBarTooltipFormatter(config.dataType, series, isComparisonChart)
      },
      legend: {
        data: series.map(s => s.name),
        ...legendConfig
      },
      xAxis: this.createVerticalBarXAxis(categories),
      yAxis: this.createVerticalBarYAxis(config.dataType, maxValue),
      series: this.createVerticalBarSeries(series, config.dataType, isComparisonChart),
      graphic: graphicConfig
    };
  }

  // ============================================================================
  // PRIVATE HELPER METHODS
  // ============================================================================

  private static processHorizontalBarData(config: ChartConfig): { categories: string[]; values: number[]; maxValue: number } {
    switch (config.type) {
      case ChartType.COUNTRY_FUNDING:
        return this.processCountryFundingData(config.data);
      case ChartType.ACQUISITION_CATEGORY:
        return this.processAcquisitionCategoryData(config.data);
      case ChartType.PRODUCT_CATEGORY:
        return this.processProductCategoryData(config.data);
      default:
        throw new Error(`Unsupported horizontal bar chart type: ${config.type}`);
    }
  }

  private static processCountryFundingData(data: any[]): {
    categories: string[];
    values: number[];
    maxValue: number;
    dealCounts?: number[];
    rawCountries?: Array<{ name: string; flag: string }>;
  } {
    const values = data.map(d => d.funding_amount || 0);
    const maxValue = Math.max(...values);
    const dealCounts = data.map(d => d.deal_count || d.count || 0);

    // Store raw country data for title generation
    // Use flag from API only - no fallback to globe emoji
    const rawCountries = data.map(d => ({
      name: d.country_name || d.label || '',
      flag: d.flag || '' // Empty string if no flag (prefer no flag over incorrect globe)
    }));

    // Format for display (flag at end with double space for alignment, only if flag exists)
    const categories = rawCountries.map(c => c.flag ? `${c.name}  ${c.flag}` : c.name);

    return { categories, values, maxValue, dealCounts, rawCountries };
  }

  private static processAcquisitionCategoryData(data: any[]): { categories: string[]; values: number[]; maxValue: number } {
    console.log('[DEBUG] processAcquisitionCategoryData input:', data);
    const values = data.map(d => d.count || 0);
    const maxValue = Math.max(...values);
    const categories = data.map(d => d.name || 'Unknown');
    console.log('[DEBUG] processAcquisitionCategoryData output:', { categories, values, maxValue });
    return { categories, values, maxValue };
  }

  private static processProductCategoryData(data: any[]): { categories: string[]; values: number[]; maxValue: number } {
    const values = data.map(d => d.amount || d.funding_amount || 0);
    const maxValue = Math.max(...values);
    const categories = data.map(d => d.name || 'Unknown');
    return { categories, values, maxValue };
  }

  private static processVerticalBarData(config: ChartConfig): { categories: string[]; values: number[]; series: any[] } {
    switch (config.type) {
      case ChartType.YEAR_OVER_YEAR:
      case ChartType.YOY_FUNDING:
      case ChartType.YOY_ACQUISITIONS:
        return this.processYearOverYearData(config.data);
      case ChartType.FUNDING_TIMESERIES:
        return this.processFundingTimeseriesData(config.data);
      case ChartType.COMPARISON:
        const timeGrouping = config.options?.timeGrouping || 'weeks';
        console.log('🔧 processVerticalBarData - COMPARISON type:', {
          timeGroupingFromOptions: config.options?.timeGrouping,
          timeGroupingUsed: timeGrouping,
          allOptions: config.options
        });
        return this.processComparisonData(config.data, timeGrouping);
      default:
        throw new Error(`Unsupported vertical bar chart type: ${config.type}`);
    }
  }

  private static processYearOverYearData(data: any): { categories: string[]; values: number[]; series: any[] } {
    return {
      categories: data.x_axis || [],
      values: [], // Not used for multi-series charts
      series: data.series || []
    };
  }

  private static processFundingTimeseriesData(data: any[]): { categories: string[]; values: number[]; series: any[] } {
    const categories = data.map(d => d.date);
    const values = data.map(d => d.funding?.amount || 0);
    return {
      categories,
      values,
      series: [{
        name: 'Funding',
        data: values,
        color: BaseChartFactory.PREMIUM_COLORS.primary
      }]
    };
  }

  private static processComparisonData(data: any, timeGrouping: 'weeks' | 'months' | 'quarters' = 'weeks'): { categories: string[]; values: number[]; series: any[] } {
    let categories = data.x_axis || data.categories || [];
    let series = data.series || [];

    console.log('🔄 processComparisonData called:', {
      timeGrouping,
      categoriesCount: categories.length,
      seriesCount: series.length,
      willAggregate: timeGrouping !== 'weeks' && categories.length > 0
    });

    // If timeGrouping is not weeks, aggregate the data
    if (timeGrouping !== 'weeks' && categories.length > 0) {
      console.log('📊 Aggregating data by:', timeGrouping);
      const aggregated = this.aggregateComparisonDataByPeriod(categories, series, timeGrouping);
      categories = aggregated.categories;
      series = aggregated.series;
      console.log('✅ Aggregation complete:', {
        newCategoriesCount: categories.length,
        newSeriesCount: series.length
      });
    }

    return {
      categories,
      values: [], // Not used for multi-series charts
      series
    };
  }

  /**
   * Aggregate comparison chart data by time period (months or quarters)
   * Groups weekly data into larger time buckets
   */
  private static aggregateComparisonDataByPeriod(
    categories: string[],
    series: any[],
    timeGrouping: 'months' | 'quarters'
  ): { categories: string[]; series: any[] } {
    // Parse week labels to extract dates
    // Week labels can be: "W1", "W2", "W29", "W29'24", "W1 (Jan 1)", etc.
    const parseWeekLabel = (label: string): Date | null => {
      // Try to extract week number and optional year
      const weekMatch = label.match(/W(\d+)(?:'(\d{2}))?/);
      if (!weekMatch) return null;

      const weekNum = parseInt(weekMatch[1]);
      const yearSuffix = weekMatch[2];

      // If year suffix exists, use it; otherwise use current year
      const year = yearSuffix ? 2000 + parseInt(yearSuffix) : new Date().getFullYear();

      // Calculate approximate date from week number
      // ISO week 1 is the first week with Thursday in the new year
      const jan4 = new Date(year, 0, 4);
      const jan4Day = jan4.getDay() || 7; // Sunday = 7
      const weekStart = new Date(jan4);
      weekStart.setDate(jan4.getDate() - jan4Day + 1 + (weekNum - 1) * 7);

      return weekStart;
    };

    // Group data by period
    const periodMap = new Map<string, { indices: number[]; label: string; sortKey: string }>();

    categories.forEach((category, index) => {
      const date = parseWeekLabel(category);
      if (!date) {
        // If we can't parse it, put it in a fallback period
        const fallbackKey = `unknown-${index}`;
        periodMap.set(fallbackKey, {
          indices: [index],
          label: category,
          sortKey: fallbackKey
        });
        return;
      }

      let periodKey: string;
      let periodLabel: string;
      let sortKey: string;

      if (timeGrouping === 'months') {
        const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
        const month = date.getMonth();
        const year = date.getFullYear();
        periodKey = `${year}-${String(month).padStart(2, '0')}`;
        periodLabel = `${monthNames[month]} ${year}`;
        sortKey = periodKey;
      } else { // quarters
        const quarter = Math.floor(date.getMonth() / 3) + 1;
        const year = date.getFullYear();
        periodKey = `${year}-Q${quarter}`;
        periodLabel = `Q${quarter} ${year}`;
        sortKey = `${year}-${quarter}`;
      }

      if (!periodMap.has(periodKey)) {
        periodMap.set(periodKey, { indices: [], label: periodLabel, sortKey });
      }
      periodMap.get(periodKey)!.indices.push(index);
    });

    // Sort periods chronologically
    const sortedPeriods = Array.from(periodMap.entries())
      .sort((a, b) => a[1].sortKey.localeCompare(b[1].sortKey));

    // Aggregate series data
    const aggregatedCategories: string[] = [];
    const aggregatedSeries = series.map(s => ({
      ...s,
      data: [] as number[],
      deal_counts: s.deal_counts ? [] as number[] : undefined
    }));

    sortedPeriods.forEach(([_, periodInfo]) => {
      aggregatedCategories.push(periodInfo.label);

      // Sum values for each series in this period
      series.forEach((s, seriesIndex) => {
        const sum = periodInfo.indices.reduce((total, idx) => {
          return total + (s.data[idx] || 0);
        }, 0);
        aggregatedSeries[seriesIndex].data.push(sum);

        // Also aggregate deal counts if present
        if (s.deal_counts && aggregatedSeries[seriesIndex].deal_counts) {
          const dealSum = periodInfo.indices.reduce((total, idx) => {
            return total + (s.deal_counts[idx] || 0);
          }, 0);
          aggregatedSeries[seriesIndex].deal_counts!.push(dealSum);
        }
      });
    });

    return { categories: aggregatedCategories, series: aggregatedSeries };
  }

  private static processPremiumFundingData(config: ChartConfig): { 
    weeks: number[]; 
    weeklyComparison: Record<number, { current: number; previous: number; currentDeals: number; previousDeals: number }>; 
    currentYear: number; 
    previousYear: number 
  } {
    const data = config.data as any[]; // WeeklyDataPoint[]
    
    // Analyze available years
    const years = Array.from(new Set(data.map(d => new Date(d.date).getFullYear()))).sort();
    const currentYear = years[years.length - 1] || new Date().getFullYear();
    const previousYear = years[years.length - 2] || currentYear - 1;
    
    const weeklyComparison: Record<number, { current: number; previous: number; currentDeals: number; previousDeals: number }> = {};
    
    data.forEach(week => {
      const year = new Date(week.date).getFullYear();
      const weekNum = week.week_number;
      
      if (!weeklyComparison[weekNum]) {
        weeklyComparison[weekNum] = { current: 0, previous: 0, currentDeals: 0, previousDeals: 0 };
      }
      
      if (year === currentYear) {
        weeklyComparison[weekNum].current = week.funding.amount / 1000000;
        weeklyComparison[weekNum].currentDeals = week.funding.count;
      } else if (year === previousYear) {
        weeklyComparison[weekNum].previous = week.funding.amount / 1000000;
        weeklyComparison[weekNum].previousDeals = week.funding.count;
      }
    });

    const weeks = Object.keys(weeklyComparison).map(Number).sort((a, b) => a - b);
    
    return { weeks, weeklyComparison, currentYear, previousYear };
  }

  private static createHorizontalBarXAxis(maxValue: number, dataType: DataType): any {
    const { max, interval } = BaseChartFactory.calculateNiceAxisScale(maxValue);
    
    return {
      type: 'value',
      max: max,
      min: 0,
      interval: interval,
      axisLabel: {
        formatter: dataType === DataType.CURRENCY ? BaseChartFactory.formatCurrency : (value: number) => value.toString(),
        fontSize: 14, // Increased for better readability
        color: BaseChartFactory.PREMIUM_COLORS.textSecondary,
        fontWeight: 600
      },
      axisLine: {
        show: true,
        lineStyle: { 
          color: '#d1d5db', // More defined x-axis line
          width: 2 
        }
      },
      axisTick: {
        show: true,
        lineStyle: {
          color: '#d1d5db',
          width: 1
        },
        length: 4
      },
      splitLine: {
        show: true,
        lineStyle: { 
          color: '#f3f4f6', 
          width: 1,
          type: 'solid'
        }
      },
      splitArea: {
        show: false
      }
    };
  }

  private static createHorizontalBarYAxis(categories: string[]): any {
    return {
      type: 'category',
      data: categories,
      inverse: true,
      axisLabel: {
        fontSize: 16,
        color: BaseChartFactory.PREMIUM_COLORS.text,
        fontWeight: 600,
        fontFamily: TYPOGRAPHY.fontFamily,
        margin: 35,
        lineHeight: 22,  // Vertical spacing between wrapped lines
        align: 'right',
        formatter: (value: string) => {
          // Wrap long category names for better readability
          if (value.length > 25) {
            const words = value.split(' ');
            let line1 = '';
            let line2 = '';
            let switchedToLine2 = false;
            let currentLength = 0;

            // Aim for roughly equal line lengths (50/50 split)
            const targetLength = Math.ceil(value.length * 0.5);

            for (const word of words) {
              // Once we switch to line2, stay on line2 to maintain word order
              if (!switchedToLine2 && (currentLength + word.length <= targetLength || !line1)) {
                line1 += (line1 ? ' ' : '') + word;
                currentLength += word.length + 1;
              } else {
                switchedToLine2 = true;
                line2 += (line2 ? ' ' : '') + word;
              }
            }

            return line2 ? `${line1}\n${line2}` : line1;
          }
          return value;
        }
      },
      axisLine: { show: false },
      axisTick: { show: false }
    };
  }

  private static createVerticalBarXAxis(categories: string[]): any {
    return {
      type: 'category',
      data: categories,
      axisLabel: {
        interval: 0,
        rotate: categories.length > 10 ? 30 : 0,
        fontSize: 16,
        fontWeight: 600,
        color: BaseChartFactory.PREMIUM_COLORS.textSecondary,
        fontFamily: TYPOGRAPHY.fontFamily,
        margin: 15 // Increased margin for better spacing
      },
      axisLine: {
        lineStyle: { color: '#e5e7eb', width: 2 } // Carta-style: clearly visible axis line
      },
      axisTick: { show: false }
    };
  }

  private static getMaxValueFromSeries(series: any[]): number {
    let maxValue = 0;
    series.forEach(s => {
      if (s.data && Array.isArray(s.data)) {
        const seriesMax = Math.max(...s.data);
        maxValue = Math.max(maxValue, seriesMax);
      }
    });
    return maxValue;
  }


  private static createVerticalBarYAxis(dataType: DataType, maxValue?: number): any {
    // Use the same 8-interval logic as horizontal bar charts
    const axisConfig: any = {
      type: 'value',
      axisLine: {
        lineStyle: { color: BaseChartFactory.PREMIUM_COLORS.border, width: 2 } // Carta-style: clearly visible axis line
      },
      axisLabel: {
        formatter: dataType === DataType.CURRENCY ? BaseChartFactory.formatCurrency : (value: number) => value.toString(),
        fontSize: 16,
        color: BaseChartFactory.PREMIUM_COLORS.text,
        fontFamily: TYPOGRAPHY.fontFamily,
        fontWeight: 'bold' // Added bold for better visibility
      },
      splitLine: {
        show: true,
        lineStyle: { 
          color: BaseChartFactory.PREMIUM_COLORS.gridLines,
          opacity: 0.5, // Make grid lines more visible
          width: 1,
          type: 'dashed' // Dashed lines for better visibility
        }
      }
    };

    // Apply 8-interval logic if maxValue is provided
    if (maxValue) {
      const { max, interval } = BaseChartFactory.calculateNiceAxisScale(maxValue);
      axisConfig.max = max;
      axisConfig.min = 0;
      axisConfig.interval = interval;
    }

    return axisConfig;
  }

  private static createHorizontalBarTooltipFormatter(dataType: DataType, data?: any[]): (params: any) => string {
    return (params: any) => {
      const paramArray = BaseChartFactory.extractTooltipParams(params);
      const param = paramArray[0];
      if (!param) return '';
      
      const value = dataType === DataType.CURRENCY 
        ? BaseChartFactory.formatCurrency(param.value)
        : param.value.toString();
      
      // Get additional data for enhanced tooltip
      const dataItem = data?.[param.dataIndex];
      const icon = dataType === DataType.CURRENCY ? '💰' : '📊';
      
      let additionalInfo = '';
      if (dataItem) {
        if (dataType === DataType.CURRENCY && dataItem.count) {
          additionalInfo = `<div style="color: ${BaseChartFactory.PREMIUM_COLORS.textSecondary}; font-size: 11px; margin-top: 4px;">
            📊 ${dataItem.count} deals
          </div>`;
        } else if (dataType === DataType.COUNT && dataItem.funding_amount) {
          additionalInfo = `<div style="color: ${BaseChartFactory.PREMIUM_COLORS.textSecondary}; font-size: 11px; margin-top: 4px;">
            💰 ${BaseChartFactory.formatCurrency(dataItem.funding_amount)} total
          </div>`;
        }
      }
      
      return `
        <div style="font-weight: 600; margin-bottom: 8px; color: ${BaseChartFactory.PREMIUM_COLORS.text};">
          ${param.name}
        </div>
        <div style="color: ${BaseChartFactory.PREMIUM_COLORS.primary};">
          ${icon} ${value}
        </div>
        ${additionalInfo}
      `;
    };
  }

  private static createVerticalBarSeries(series: any[], dataType: DataType, enableStacking?: boolean): any[] {
    return series.map((s, index) => ({
      name: s.name,
      type: 'bar',
      stack: enableStacking ? (s.stack || 'total') : undefined, // Support stacking for comparison charts
      data: s.data,
      itemStyle: {
        color: s.color || BaseChartFactory.PREMIUM_COLORS.primary,
        // For stacked charts, only round the top bar
        borderRadius: enableStacking
          ? (index === series.length - 1 ? [2, 2, 0, 0] : [0, 0, 0, 0])
          : [2, 2, 0, 0]
      },
      emphasis: {
        itemStyle: {
          color: index === 0 ? '#333333' : '#7b7bff',
          shadowBlur: 10,
          shadowOffsetX: 0,
          shadowColor: 'rgba(0, 0, 0, 0.3)'
        }
      },
      label: {
        // For stacked charts, hide individual labels to avoid clutter
        show: !enableStacking,
        position: 'top',
        formatter: (params: any) => {
          // For M&A charts (count type), show the value itself (deal count)
          if (dataType === DataType.COUNT && params.value > 0) {
            return params.value === 1 ? '1' : `${params.value}`;
          }
          // For funding charts, show deal counts if available
          if (dataType === DataType.CURRENCY && s.deal_counts && s.deal_counts[params.dataIndex]) {
            const count = s.deal_counts[params.dataIndex];
            return count === 1 ? '1' : `${count}`;
          }
          // Fallback: show the value if it's greater than 0
          if (params.value > 0) {
            return params.value === 1 ? '1' : `${params.value}`;
          }
          return '';
        },
        fontSize: 14,
        fontWeight: 600,
        color: s.color || BaseChartFactory.PREMIUM_COLORS.primary,
        distance: 8
      },
        barMaxWidth: BaseChartFactory.CHART_TYPE_STANDARDS.verticalBar.bar.width,
      barGap: '15%',
      barCategoryGap: '30%',
      animationDuration: 800,
      animationDelay: index * 100
    }));
  }

  private static createVerticalBarTooltipFormatter(dataType: DataType, series: any[], enableStacking?: boolean): (params: any) => string {
    return (params: any) => {
      const paramArray = BaseChartFactory.extractTooltipParams(params);
      if (paramArray.length === 0) return '';

      let tooltip = `<div style="font-weight: 600; margin-bottom: 8px; color: ${BaseChartFactory.PREMIUM_COLORS.text};">
        ${paramArray[0].axisValueLabel}
      </div>`;

      // Calculate total for stacked charts
      const total = enableStacking
        ? paramArray.reduce((sum: number, param: any) => sum + (param.value || 0), 0)
        : 0;

      paramArray.forEach((param: any) => {
        const value = dataType === DataType.CURRENCY
          ? BaseChartFactory.formatCurrency(param.value)
          : param.value.toString();

        const icon = dataType === DataType.CURRENCY ? '💰' : '📊';

        // For stacked charts, show percentage
        let percentageText = '';
        if (enableStacking && total > 0) {
          const percentage = ((param.value / total) * 100).toFixed(1);
          percentageText = ` (${percentage}%)`;
        }

        tooltip += `<div style="color: ${param.color}; margin-bottom: 4px;">
          ${icon} ${param.seriesName}: ${value}${percentageText}
        </div>`;
      });

      // Add total line for stacked charts
      if (enableStacking && total > 0) {
        const totalFormatted = dataType === DataType.CURRENCY
          ? BaseChartFactory.formatCurrency(total)
          : total.toString();
        tooltip += `<div style="border-top: 1px solid ${BaseChartFactory.PREMIUM_COLORS.border}; margin-top: 8px; padding-top: 8px; font-weight: 600;">
          Total: ${totalFormatted}
        </div>`;
      }

      return tooltip;
    };
  }

  private static createPremiumFundingTooltipFormatter(weeklyComparison: Record<number, any>): (params: any) => string {
    return (params: any) => {
      const paramArray = BaseChartFactory.extractTooltipParams(params);
      if (paramArray.length < 2) return '';
      
      const weekNum = paramArray[0].axisValueLabel;
      const current = paramArray[0]?.value || 0;
      const previous = paramArray[1]?.value || 0;
      const growth = this.calculateSafeGrowthRate(previous, current);
      const growthIcon = growth >= 0 ? '📈' : '📉';
      
      return `
        <div style="font-family: ${TYPOGRAPHY.fontFamily}; font-weight: 600; font-size: 18px; color: ${BaseChartFactory.PREMIUM_COLORS.text}; margin-bottom: 12px;">
          ${growthIcon} Week ${weekNum} Funding
        </div>
        <div style="display: flex; justify-content: space-between; margin-bottom: 8px;">
          <span style="color: ${BaseChartFactory.PREMIUM_COLORS.primary}; font-weight: 500;">Current Period:</span>
          <span style="font-weight: 600; color: ${BaseChartFactory.PREMIUM_COLORS.text};">$${current.toFixed(1)}M</span>
        </div>
        <div style="display: flex; justify-content: space-between; margin-bottom: 8px;">
          <span style="color: ${BaseChartFactory.PREMIUM_COLORS.secondary}; font-weight: 500;">Previous Period:</span>
          <span style="font-weight: 600; color: ${BaseChartFactory.PREMIUM_COLORS.text};">$${previous.toFixed(1)}M</span>
        </div>
        <div style="padding-top: 8px; border-top: 1px solid ${BaseChartFactory.PREMIUM_COLORS.border};">
          <span style="color: ${growth >= 0 ? BaseChartFactory.PREMIUM_COLORS.accent : BaseChartFactory.PREMIUM_COLORS.danger}; font-weight: 600; font-size: 16px;">
            ${growth >= 0 ? '↗' : '↘'} ${growth > 0 ? '+' : ''}${growth.toFixed(1)}% YoY Growth
          </span>
        </div>
      `;
    };
  }

  private static calculateSafeGrowthRate(previous: number, current: number): number {
    if (previous === 0) return current > 0 ? 100 : 0;
    return ((current - previous) / previous) * 100;
  }

}

// ============================================================================
// UNIFIED CHART FACTORY (MAIN ENTRY POINT)
// ============================================================================

export class UnifiedChartFactory {
  /**
   * Create chart configuration based on type
   * This is the main entry point for all chart creation
   */
  static createChart(config: ChartConfig): EChartsOption {
    switch (config.type) {
      case ChartType.COUNTRY_FUNDING:
      case ChartType.ACQUISITION_CATEGORY:
      case ChartType.PRODUCT_CATEGORY:
        return ChartTypeFactory.createHorizontalBarChart(config);

      case ChartType.YEAR_OVER_YEAR:
      case ChartType.FUNDING_TIMESERIES:
      case ChartType.COMPARISON:
      case ChartType.YOY_FUNDING:
      case ChartType.YOY_ACQUISITIONS:
        return ChartTypeFactory.createVerticalBarChart(config);

      default:
        throw new Error(`Unsupported chart type: ${config.type}`);
    }
  }

  /**
   * Backward compatibility method for existing chart creation
   * This allows existing code to continue working during migration
   * Now with enhanced dynamic title generation enabled by default
   */
  static createCountryFundingChart(
    data: any[], 
    title: string, 
    subtitle?: string, 
    options?: {
      timeframe?: '1w' | '2w' | '4w' | '12w' | '6m' | 'ytd' | 'ttm' | 'custom';
      chartContext?: 'default' | 'email' | 'social-media' | 'presentation';
      timeDomain?: any;
    }
  ): EChartsOption {
    return this.createChart({
      type: ChartType.COUNTRY_FUNDING,
      orientation: ChartOrientation.HORIZONTAL,
      dataType: DataType.CURRENCY,
      title,
      subtitle,
      data,
      options: {
        enableDynamicTitles: true,
        timeframe: options?.timeframe || 'custom',
        chartContext: options?.chartContext || 'default',
        timeDomain: options?.timeDomain
      }
    });
  }

  static createYearOverYearChart(
    data: any, 
    title: string, 
    subtitle?: string, 
    valueType: 'currency' | 'count' = 'currency',
    options?: {
      timeframe?: '1w' | '2w' | '4w' | '12w' | '6m' | 'ytd' | 'ttm' | 'custom';
      isMA?: boolean;
      isTTM?: boolean;
      timeGrouping?: 'weeks' | 'months' | 'quarters';
      yearVisibility?: any;
      weekOffset?: number;
    }
  ): EChartsOption {
    return this.createChart({
      type: ChartType.YEAR_OVER_YEAR,
      orientation: ChartOrientation.VERTICAL,
      dataType: valueType === 'currency' ? DataType.CURRENCY : DataType.COUNT,
      title,
      subtitle,
      data,
      options: {
        enableDynamicTitles: true,
        timeframe: options?.timeframe || 'custom',
        isMA: options?.isMA || false,
        isTTM: options?.isTTM || false,
        timeGrouping: options?.timeGrouping || 'weeks',
        yearVisibility: options?.yearVisibility,
        weekOffset: options?.weekOffset || 0
      }
    });
  }

  static createAcquisitionCategoryChart(
    data: any[],
    title: string,
    subtitle?: string,
    options?: {
      timeframe?: '1w' | '2w' | '4w' | '12w' | '6m' | 'ytd' | 'ttm' | 'custom';
      weekOffset?: number;
      chartContext?: 'default' | 'email' | 'social-media' | 'presentation';
    }
  ): EChartsOption {
    return this.createChart({
      type: ChartType.ACQUISITION_CATEGORY,
      orientation: ChartOrientation.HORIZONTAL,
      dataType: DataType.COUNT,
      title,
      subtitle,
      data,
      options: {
        enableDynamicTitles: true,
        timeframe: options?.timeframe || '12w',
        weekOffset: options?.weekOffset || 0,
        chartContext: options?.chartContext || 'default',
        isMA: true // Acquisition charts are always M&A
      }
    });
  }

  static createProductCategoryChart(
    data: any[], 
    title: string, 
    subtitle?: string,
    options?: {
      timeframe?: '1w' | '2w' | '4w' | '12w' | '6m' | 'ytd' | 'ttm' | 'custom';
      weekOffset?: number;
      chartContext?: 'default' | 'email' | 'social-media' | 'presentation';
    }
  ): EChartsOption {
    return this.createChart({
      type: ChartType.PRODUCT_CATEGORY,
      orientation: ChartOrientation.HORIZONTAL,
      dataType: DataType.CURRENCY,
      title,
      subtitle,
      data,
      options: {
        enableDynamicTitles: true,
        timeframe: options?.timeframe || 'custom',
        weekOffset: options?.weekOffset || 0,
        chartContext: options?.chartContext || 'default'
      }
    });
  }

  // Premium chart backward compatibility methods
  static createPremiumFundingChart(
    data: any[], 
    config: any,
    options?: {
      timeframe?: '1w' | '2w' | '4w' | '12w' | '6m' | 'ytd' | 'ttm' | 'custom';
      isMA?: boolean;
      isTTM?: boolean;
      timeGrouping?: 'weeks' | 'months' | 'quarters';
    }
  ): EChartsOption {
    return this.createChart({
      type: ChartType.YOY_FUNDING,
      orientation: ChartOrientation.VERTICAL,
      dataType: DataType.CURRENCY,
      title: 'Premium Funding Analysis',
      subtitle: 'Year-over-Year Funding Trends',
      data,
      options: {
        enableDynamicTitles: true,
        timeframe: options?.timeframe || 'custom',
        isMA: options?.isMA || false,
        isTTM: options?.isTTM || false,
        timeGrouping: options?.timeGrouping || 'weeks'
      }
    });
  }

  static createPremiumAcquisitionsChart(
    data: any[], 
    config: any,
    options?: {
      timeframe?: '1w' | '2w' | '4w' | '12w' | '6m' | 'ytd' | 'ttm' | 'custom';
      isMA?: boolean;
      isTTM?: boolean;
      timeGrouping?: 'weeks' | 'months' | 'quarters';
    }
  ): EChartsOption {
    return this.createChart({
      type: ChartType.YOY_ACQUISITIONS,
      orientation: ChartOrientation.VERTICAL,
      dataType: DataType.COUNT,
      title: 'Premium M&A Analysis',
      subtitle: 'Year-over-Year Acquisition Trends',
      data,
      options: {
        enableDynamicTitles: true,
        timeframe: options?.timeframe || 'custom',
        isMA: options?.isMA || true,
        isTTM: options?.isTTM || false,
        timeGrouping: options?.timeGrouping || 'weeks'
      }
    });
  }

  /**
   * Utility method for vertical bar chart grid configuration
   * Updated to allow x-axis to expand more within the image
   */
  static createVerticalBarChartGrid(): any {
    return {
      left: '1%', // Minimal left margin to allow x-axis to expand more
      right: '1%', // Minimal right margin to give more space for chart content
      top: '20%',
      bottom: '20%', // Increased from 15% to give more space for x-axis labels
      containLabel: true
    };
  }

  /**
   * Calculate optimal chart height for category charts
   * Using 75% of the calculated height for better proportions
   */
  static calculateCategoryChartHeight(categoryCount: number): number {
    const baseHeight = Math.max(500, categoryCount * 70 + 220);
    return Math.round(baseHeight * 0.75);
  }

  /**
   * Helper to get max stacked value from series data (for comparison/stacked charts)
   * Calculates the maximum sum of values across all series for any single category
   */
  private static getMaxValueFromSeriesData(series: any[]): number {
    let maxStackedValue = 0;

    // For stacked charts, we need to calculate the max stacked height per category
    if (series.length > 0 && series[0].data) {
      const categoryCount = series[0].data.length;

      for (let i = 0; i < categoryCount; i++) {
        let stackedValue = 0;
        series.forEach(s => {
          if (s.data && s.data[i]) {
            stackedValue += s.data[i];
          }
        });
        maxStackedValue = Math.max(maxStackedValue, stackedValue);
      }
    }

    return maxStackedValue;
  }
}
